import { Injectable } from '@angular/core'
import { environment } from '../../environments/environment'

declare global {
    interface Window {
        gtag: (...args: any[]) => void
        fbq: (...args: any[]) => void
        dataLayer: any[]
    }
}

@Injectable({
    providedIn: 'root',
})
export class ModernAnalyticsService {
    private isGA4Initialized = false
    private adUserDataDenied = false

    constructor() {
        // Initialize dataLayer if not exists
        window.dataLayer = window.dataLayer || []
        // Initialize GA4 with environment-specific measurement ID
        this.initializeGA4()
    }

    /**
     * Safe wrapper for Facebook Pixel calls that catches and logs errors
     * CRITICAL: This ensures tracking failures never block user flows
     * Common errors handled:
     * - Pixel not initialized/unavailable
     * - Invalid email format passed to pixel
     * - Network errors
     */
    private safePixelCall(eventName: string, params: any): void {
        try {
            if (!environment.production) return
            if (typeof window.fbq !== 'undefined' && typeof window.fbq === 'function') {
                window.fbq('track', eventName, params)
            }
        } catch (error) {
            console.warn(`[Meta Pixel] ${eventName} failed (non-blocking):`, error)
        }
    }

    /**
     * Dynamically load and initialize GA4 with environment-specific measurement ID
     */
    private initializeGA4() {
        if (this.isGA4Initialized || !environment.googleAnalyticsKey) return
        // Establish the queue BEFORE loading the network script: fast signup/checkout events
        // must survive a slow tag load, and existing consent commands must retain their order.
        window.gtag = window.gtag || function () { window.dataLayer.push(arguments) }
        window.gtag('js', new Date())
        window.gtag('config', environment.googleAnalyticsKey, {
            cookie_flags: 'SameSite=None;Secure', debug_mode: !environment.production, send_page_view: true,
            enhanced_measurement: true,
            custom_map: { custom_parameter_1: 'parking_type', custom_parameter_2: 'booking_source' },
        })
        if (environment.production && environment.googleAdsTagId) {
            window.gtag('config', environment.googleAdsTagId, { allow_enhanced_conversions: true, send_page_view: false })
        }
        this.isGA4Initialized = true
        const script = document.createElement('script')
        script.async = true
        script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsKey}`
        script.onerror = () => console.warn('Google tag could not load')
        document.head.appendChild(script)
    }

    /** Send matching data only for customer-submitted forms, never as a GA4 event parameter. */
    async trackEnhancedLead(email?: string | null): Promise<void> {
        if (!environment.production || !environment.googleAdsTagId || this.adUserDataDenied
            || /^\/(admin|merchant)(\/|$)/.test(window.location.pathname) || !email) return
        try {
            const parts = email.toLowerCase().replace(/\s/g, '').split('@')
            if (parts.length !== 2 || !parts[0] || !parts[1].includes('.')) return
            if (parts[1] === 'gmail.com' || parts[1] === 'googlemail.com') parts[0] = parts[0].split('+')[0].replace(/\./g, '')
            if (!parts[0]) return
            const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(parts.join('@')))
            if (this.adUserDataDenied) return
            const hash = Array.from(new Uint8Array(bytes)).map(byte => byte.toString(16).padStart(2, '0')).join('')
            window.gtag('set', 'user_data', { sha256_email_address: hash })
            window.gtag('event', 'form_submit', { send_to: environment.googleAdsTagId })
            // Limit the data to this form event; do not retain it for another customer's session.
            window.gtag('set', 'user_data', null)
        } catch (_) {
            // Analytics must never prevent a successful registration, enquiry or checkout.
        }
    }

    /**
     * Track custom events with GA4
     */
    trackEvent(eventName: string, parameters: { [key: string]: any } = {}) {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', eventName, {
                ...parameters,
                // Add default parameters
                timestamp: new Date().toISOString(),
                page_location: window.location.href,
                page_title: document.title,
            })
        }
    }

    /**
     * Track page views (automatic with gtag config, but can be called manually)
     */
    trackPageView(pagePath?: string, pageTitle?: string) {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('config', environment.googleAnalyticsKey, {
                page_path: pagePath || window.location.pathname,
                page_title: pageTitle || document.title,
            })
        }
    }

    /**
     * Track e-commerce events (parking bookings)
     */
    trackPurchase(transactionId: string, value: number, currency: string = 'ZAR', items: any[] = []) {
        // console.log(`📊 [GA4] Purchase tracked: ID=${transactionId}, Value=${currency} ${value}, Items:`, items)

        // GA4 Purchase Event
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'purchase', {
                transaction_id: transactionId,
                value: value,
                currency: currency,
                items: items,
            })
        }

        // Facebook Pixel Purchase Event (fire-and-forget with error handling)
        this.safePixelCall('Purchase', {
            value: value,
            currency: currency,
            content_type: 'parking_booking',
            content_ids: items.map((item) => item.item_id),
        })
    }

    /**
     * Track add to cart events
     */
    trackAddToCart(itemId: string, itemName: string, value: number, currency: string = 'ZAR') {
        // console.log(`📊 [GA4] Add to Cart tracked: ${itemName} (${currency} ${value})`)

        // GA4 Add to Cart Event
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'add_to_cart', {
                currency: currency,
                value: value,
                items: [
                    {
                        item_id: itemId,
                        item_name: itemName,
                        item_category: 'parking',
                        quantity: 1,
                        price: value,
                    },
                ],
            })
        }

        // Facebook Pixel Add to Cart Event (fire-and-forget with error handling)
        this.safePixelCall('AddToCart', {
            value: value,
            currency: currency,
            content_type: 'parking_space',
            content_id: itemId,
            content_name: itemName,
        })
    }

    /**
     * Track lead generation (parking not found form)
     */
    trackLead(leadSource: string = 'parking_not_found') {
        // GA4 Lead Event
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'generate_lead', {
                lead_source: leadSource,
                value: 1,
                currency: 'ZAR',
            })
        }

        // Facebook Pixel Lead Event (fire-and-forget with error handling)
        this.safePixelCall('Lead', {
            content_category: 'parking_inquiry',
            content_name: leadSource,
        })
    }

    /**
     * Track user engagement events
     */
    trackEngagement(action: string, category: string = 'user_interaction', label?: string, value?: number) {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', action, {
                event_category: category,
                event_label: label,
                value: value,
            })
        }
    }

    /**
     * Track search events
     */
    trackSearch(searchTerm: string, resultsCount?: number) {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'search', {
                search_term: searchTerm,
                results_count: resultsCount,
            })
        }
    }

    /**
     * Set user properties for better segmentation
     */
    setUserProperties(properties: { [key: string]: any }) {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('config', environment.googleAnalyticsKey, {
                user_properties: properties,
            })
        }
    }

    /**
     * Track custom conversions
     */
    trackConversion(conversionName: string, value?: number, currency: string = 'ZAR') {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'conversion', {
                send_to: `${environment.googleAnalyticsKey}/${conversionName}`,
                value: value,
                currency: currency,
            })
        }
    }

    /**
     * Privacy-compliant user consent management
     */
    updateConsent(consentSettings: {
        ad_user_data?: 'granted' | 'denied'
        ad_personalization?: 'granted' | 'denied'
        analytics_storage?: 'granted' | 'denied'
        ad_storage?: 'granted' | 'denied'
        functionality_storage?: 'granted' | 'denied'
        personalization_storage?: 'granted' | 'denied'
        security_storage?: 'granted' | 'denied'
    }) {
        if (consentSettings.ad_user_data) this.adUserDataDenied = consentSettings.ad_user_data === 'denied'
        if (typeof window.gtag !== 'undefined') {
            window.gtag('consent', 'update', consentSettings)
        }
    }

    // ========================================
    // PARKING-SPECIFIC E-COMMERCE FUNNEL TRACKING
    // ========================================

    /**
     * Track parking bay view as product view (Step 1 of funnel)
     */
    trackParkingView(parking: { id: string; title: string; monthlyPrice: number; location: string; parkingType: string; amenities?: string[]; currency?: string }) {
        const currency = parking.currency || 'ZAR'
        const item = {
            item_id: parking.id,
            item_name: parking.title,
            item_category: 'parking_bay',
            item_category2: parking.parkingType,
            item_brand: 'ParkUpp',
            price: parking.monthlyPrice,
            quantity: 1,
            item_location_id: parking.location,
            custom_parameters: {
                amenities: parking.amenities?.join(',') || '',
                parking_type: parking.parkingType,
            },
        }

        // GA4 View Item Event
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'view_item', {
                currency: currency,
                value: parking.monthlyPrice,
                items: [item],
            })
        }

        // Facebook Pixel ViewContent Event (fire-and-forget with error handling)
        this.safePixelCall('ViewContent', {
            content_type: 'parking_bay',
            content_ids: [parking.id],
            content_name: parking.title,
            content_category: parking.parkingType,
            value: parking.monthlyPrice,
            currency: currency,
            custom_data: {
                location: parking.location,
                amenities: parking.amenities?.join(',') || '',
            },
        })

        // console.log(`📊 Tracked parking view: ${parking.title} (${currency} ${parking.monthlyPrice})`)
    }

    /**
     * Track when user starts parking request process (Step 2 of funnel)
     */
    trackParkingRequestStart(parking: { id: string; title: string; monthlyPrice: number; parkingType: string; currency?: string }) {
        const currency = parking.currency || 'ZAR'

        // GA4 Begin Checkout Event
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'begin_checkout', {
                currency: currency,
                value: parking.monthlyPrice,
                items: [
                    {
                        item_id: parking.id,
                        item_name: parking.title,
                        item_category: 'parking_bay',
                        item_category2: parking.parkingType,
                        price: parking.monthlyPrice,
                        quantity: 1,
                    },
                ],
            })
        }

        // Facebook Pixel InitiateCheckout Event (fire-and-forget with error handling)
        this.safePixelCall('InitiateCheckout', {
            content_type: 'parking_bay',
            content_ids: [parking.id],
            content_name: parking.title,
            value: parking.monthlyPrice,
            currency: currency,
            num_items: 1,
        })

        // console.log(`📊 Tracked parking request start: ${parking.title}`)
    }

    /**
     * Track user signup during parking request (Step 3 of funnel)
     */
    trackParkingUserSignup(
        parking: {
            id: string
            title: string
            monthlyPrice: number
            currency?: string
        },
        signupMethod: 'email' | 'google' | 'facebook' = 'email'
    ) {
        const currency = parking.currency || 'ZAR'

        // GA4 Sign Up Event
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'sign_up', {
                method: signupMethod,
                currency: currency,
                value: parking.monthlyPrice,
                custom_parameters: {
                    parking_id: parking.id,
                    parking_title: parking.title,
                    signup_context: 'parking_request',
                },
            })
        }

        // Facebook Pixel CompleteRegistration Event (fire-and-forget with error handling)
        this.safePixelCall('CompleteRegistration', {
            content_name: parking.title,
            value: parking.monthlyPrice,
            currency: currency,
            status: 'completed',
            custom_data: {
                signup_method: signupMethod,
                parking_id: parking.id,
                context: 'parking_request',
            },
        })

        // console.log(`📊 Tracked user signup: ${signupMethod} for ${parking.title}`)
    }

    /**
     * Track payment method addition (Step 4 of funnel)
     */
    trackPaymentMethodAdded(
        parking: {
            id: string
            title: string
            monthlyPrice: number
            currency?: string
        },
        paymentMethod: 'credit_card' | 'debit_card' | 'bank_transfer' = 'credit_card'
    ) {
        const currency = parking.currency || 'ZAR'

        // GA4 Add Payment Info Event
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'add_payment_info', {
                currency: currency,
                value: parking.monthlyPrice,
                payment_type: paymentMethod,
                items: [
                    {
                        item_id: parking.id,
                        item_name: parking.title,
                        item_category: 'parking_bay',
                        price: parking.monthlyPrice,
                        quantity: 1,
                    },
                ],
            })
        }

        // Facebook Pixel AddPaymentInfo Event (fire-and-forget with error handling)
        this.safePixelCall('AddPaymentInfo', {
            content_category: 'parking_bay',
            content_ids: [parking.id],
            content_name: parking.title,
            value: parking.monthlyPrice,
            currency: currency,
            custom_data: {
                payment_method: paymentMethod,
            },
        })

        // console.log(`📊 Tracked payment method added: ${paymentMethod} for ${parking.title}`)
    }

    /**
     * Track parking search with results (for funnel analysis)
     */
    trackParkingSearch(searchParams: { location: string; dateFrom?: string; dateTo?: string; parkingType?: string; resultsCount: number; averagePrice?: number }) {
        // GA4 Search Event
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'search', {
                search_term: searchParams.location,
                results_count: searchParams.resultsCount,
                custom_parameters: {
                    date_from: searchParams.dateFrom || '',
                    date_to: searchParams.dateTo || '',
                    parking_type: searchParams.parkingType || 'all',
                    average_price: searchParams.averagePrice || 0,
                },
            })
        }

        // Facebook Pixel Search Event (fire-and-forget with error handling)
        this.safePixelCall('Search', {
            search_string: searchParams.location,
            content_category: 'parking_search',
            custom_data: {
                results_count: searchParams.resultsCount,
                parking_type: searchParams.parkingType || 'all',
                date_range: `${searchParams.dateFrom || ''} to ${searchParams.dateTo || ''}`,
            },
        })

        // console.log(`📊 Tracked parking search: ${searchParams.location} (${searchParams.resultsCount} results)`)
    }

    /**
     * Track funnel abandonment for analysis
     */
    trackFunnelAbandonment(
        step: 'view' | 'request_start' | 'signup' | 'payment_info' | 'purchase',
        parking: {
            id: string
            title: string
            monthlyPrice: number
            currency?: string
        },
        reason?: string
    ) {
        const currency = parking.currency || 'ZAR'

        // GA4 Custom Event for Abandonment
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'funnel_abandonment', {
                currency: currency,
                value: parking.monthlyPrice,
                custom_parameters: {
                    funnel_step: step,
                    parking_id: parking.id,
                    parking_title: parking.title,
                    abandonment_reason: reason || 'unknown',
                },
            })
        }

        // console.log(`📊 Tracked funnel abandonment at ${step}: ${parking.title} (${reason || 'unknown reason'})`)
    }

    /**
     * Track user engagement with parking amenities/features
     */
    trackParkingFeatureInteraction(
        parking: {
            id: string
            title: string
        },
        feature: string,
        action: 'view' | 'click' | 'expand'
    ) {
        // GA4 Custom Event
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'parking_feature_interaction', {
                custom_parameters: {
                    parking_id: parking.id,
                    parking_title: parking.title,
                    feature_name: feature,
                    interaction_type: action,
                },
            })
        }

        // console.log(`📊 Tracked feature interaction: ${feature} (${action}) on ${parking.title}`)
    }
}
